import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Package, ProductInterface } from '@vendasta/marketplace-packages';
import { SubscriptionList } from '@vendasta/rx-utils';
import { DateRange } from '@vendasta/uikit';
import { addWeeks, addYears, startOfMonth } from 'date-fns';
import { Observable, ReplaySubject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, shareReplay, skipWhile, startWith } from 'rxjs/operators';
import { AccountFilterComponent } from '../../../../filters/account-filter/account-filter.component';
import { AccountFilterService } from '../../../../filters/account-filter/account-filter.service';
import {
  ActualCloseFilterService,
  AssignedDateFilterService,
  CreatedDateFilterService,
  DateFilterServiceInterface,
  ExpectedCloseFilterService,
} from '../../../../filters/date-filters/date-filters.service';
import {
  DateRangeSelectorDialogComponent,
  DateRangeSelectorDialogData,
} from '../../../../filters/date-range-selector-dialog/date-range-selector-dialog.component';
import { SalesTeamOption } from '../../../../filters/filter-options/sales-team-option.model';
import { SalespersonOption } from '../../../../filters/filter-options/salesperson-option.model';
import { OpportunityTypeFilterComponent } from '../../../../filters/opportunity-type-filter/opportunity-type-filter.component';
import { OpportunityTypeFilterService } from '../../../../filters/opportunity-type-filter/opportunity-type-filter.service';
import { PipelineFilterPackageSelectionService } from '../../../../filters/package-selector/pipeline-filter-package-selection.service';
import { PipelineFilterPackageSelectorComponent } from '../../../../filters/package-selector/pipeline-filter-package-selector.component';
import { ProductSelectorComponent } from '../../../../filters/product-selector/product-selector.component';
import { ProductSelectorService } from '../../../../filters/product-selector/product-selector.service';
import { SalespersonMultiSelectComponent } from '../../../../filters/salesperson-multi-select/salesperson-multi-select.component';
import { SalespersonMultiSelectStoreService } from '../../../../filters/salesperson-multi-select/salesperson-multi-select.store.service';
import { SalesTeamMultiSelectStoreService } from '../../../../filters/salesteam-multi-select/sales-team-multi-select-store.service';
import { SalesTeamMultiSelectComponent } from '../../../../filters/salesteam-multi-select/sales-team-multi-select.component';
import { SALES_TEAMS_SERVICE_TOKEN, SalesTeamsService } from '../../../../filters/salesteam-multi-select/tokens';
import { TagMultiSelectionStoreService } from '../../../../filters/tag-selector/tag-multi-selection.service';
import { TagMultiSelectComponent } from '../../../../filters/tag-selector/tag-multi-selector.component';
import { PipelineBoardService } from '../../../pipeline-board-service/pipeline-board.service';
import { PipelineStatusCheckboxComponent } from '../../../pipeline-status-checkbox/pipeline-status-checkbox.component';
import {
  PipelineStatusCheckboxService,
  PipelineStatuses,
} from '../../../pipeline-status-checkbox/pipeline-status-checkbox.service';
import { HAS_ACCESS_TO_ALL_ACCOUNTS_IN_MARKET_TOKEN, MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '../../../tokens';

const NO_SALESPEOPLE_IN_TEAM_PLACEHOLDER = 'NO-ID';

interface UserData {
  partnerId: string;
  marketId: string;
}

@Component({
  selector: 'sales-ui-pipeline-board-filter-chips',
  templateUrl: './pipeline-board-filter-chips.component.html',
  styleUrls: ['./pipeline-board-filter-chips.scss'],
})
export class PipelineBoardFilterChipsComponent implements OnDestroy {
  selectedSalespeopleNames$: Observable<string>;
  selectedSalesTeamNames$: Observable<string>;
  showSalespeopleAndTeams$: Observable<boolean>;
  private readonly subscriptions = SubscriptionList.new();
  readonly expectedCloseFilter$ = this.expectedCloseFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );
  readonly isExpectedCloseFilterSet$ = this.expectedCloseFilter$.pipe(
    map((boardFilter: DateRange) => !!boardFilter?.start && !!boardFilter?.end),
  );
  readonly expectedCloseFilterDateRange$ = this.expectedCloseFilterService.dateFilterToString$;

  readonly actualCloseFilter$: Observable<DateRange> = this.actualCloseFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );
  readonly isActualCloseFilterSet$ = this.actualCloseFilter$.pipe(
    map((boardFilter: DateRange) => !!boardFilter?.start && !!boardFilter?.end),
  );
  readonly actualCloseFilterToString$ = this.actualCloseFilterService.dateFilterToString$;
  readonly selectedAccountGroupName$ = this.accountFilterService.selectedAccountGroupName$;

  readonly selectedTypes$ = this.opportunityTypeFilterService.selectedTypes$;
  readonly selectedTypeNames$ = this.opportunityTypeFilterService.selectedTypeNames$;

  readonly createdDateFilter$: Observable<DateRange> = this.createdDateFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );
  readonly isCreatedDateFilterSet$ = this.createdDateFilter$.pipe(
    map((boardFilter: DateRange) => !!boardFilter?.start && !!boardFilter?.end),
  );
  readonly createdDateFilterToString$: Observable<string> = this.createdDateFilterService.dateFilterToString$;

  readonly assignedDateFilter$: Observable<DateRange> = this.assignedDateFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );
  readonly isAssignedDateFilterSet$ = this.assignedDateFilter$.pipe(
    map((boardFilter: DateRange) => !!boardFilter?.start && !!boardFilter?.end),
  );
  readonly assignedDateFilterToString$: Observable<string> = this.assignedDateFilterService.dateFilterToString$;

  private readonly today = new Date();
  private readonly CALENDAR_DEFAULTS = {
    TODAY: this.today,
    ADD_ONE_WEEK: addWeeks(this.today, 1),
    ADD_TWO_YEARS: addYears(this.today, 2),
    START_OF_MONTH: startOfMonth(this.today),
  };
  readonly selectedPackages$: Observable<Package[]>;
  readonly selectedPackagesNames$: Observable<string>;
  readonly selectedProducts$: Observable<ProductInterface[]>;
  readonly selectedProductNames$: Observable<string>;
  readonly selectedStatusesNames$: Observable<string>;
  private readonly selectedTags$$ = new ReplaySubject<string[]>(1);
  readonly selectedTags$: Observable<string[]> = this.selectedTags$$.asObservable();
  private readonly selectedStatuses$: Observable<PipelineStatuses> = this.statusesFilterStore.statuses$;
  readonly hasSelectedStatuses$: Observable<boolean> = this.selectedStatuses$.pipe(
    map((s: PipelineStatuses): boolean => s?.won || s?.lost || s?.open),
  );
  readonly userData$: Observable<UserData>;

  constructor(
    private readonly dialog: MatDialog,
    private readonly spStore: SalespersonMultiSelectStoreService,
    private readonly tagStore: TagMultiSelectionStoreService,
    private readonly stStore: SalesTeamMultiSelectStoreService,
    @Inject(SALES_TEAMS_SERVICE_TOKEN) private salesTeamsService: SalesTeamsService,
    private readonly store: PipelineBoardService,
    private readonly expectedCloseFilterService: ExpectedCloseFilterService,
    private readonly actualCloseFilterService: ActualCloseFilterService,
    private readonly createdDateFilterService: CreatedDateFilterService,
    private readonly assignedDateFilterService: AssignedDateFilterService,
    private readonly pkgStore: PipelineFilterPackageSelectionService,
    private readonly productSelectorService: ProductSelectorService,
    private readonly accountFilterService: AccountFilterService,
    private readonly opportunityTypeFilterService: OpportunityTypeFilterService,
    @Inject(HAS_ACCESS_TO_ALL_ACCOUNTS_IN_MARKET_TOKEN) private hasAccessToAllAccountsInMarket$: Observable<boolean>,
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) readonly marketId$: Observable<string>,
    private readonly statusesFilterStore: PipelineStatusCheckboxService,
    private readonly i18n: TranslateService,
  ) {
    this.userData$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      map(([partnerId, marketId]) => {
        return {
          partnerId: partnerId,
          marketId: marketId,
        };
      }),
    );
    this.selectedSalespeopleNames$ = this.spStore.selectedSalespeople$.pipe(
      map((sp: SalespersonOption[]): string => {
        return sp.map((s) => s?.fullName).join(', ');
      }),
    );

    this.subscriptions.add(this.salesTeamsService.arrayOfSalesTeamMembers$, (arrayOfSalesPersonIds: string[][]) => {
      return this.filterBySalesTeamMemberIds(arrayOfSalesPersonIds);
    });

    this.selectedSalesTeamNames$ = this.stStore.selectedSalesTeams$.pipe(
      map((sp: SalesTeamOption[]): string => sp.map((s) => s.teamName).join(', ')),
    );

    this.showSalespeopleAndTeams$ = this.hasAccessToAllAccountsInMarket$;
    this.subscriptions.add(
      combineLatest([this.tagStore.selectedOppTags$, this.tagStore.selectedAccountTags$]),
      ([oppTags, accTags]) => {
        if ((oppTags || []).length === 0 && (accTags || []).length === 0) {
          this.selectedTags$$.next(null);
        } else {
          this.selectedTags$$.next(oppTags.concat(accTags).map((t) => t.name));
        }
        this.dialog.closeAll();
      },
    );
    this.selectedPackages$ = this.pkgStore.selectedPackages$;
    this.selectedPackagesNames$ = this.selectedPackages$.pipe(
      filter((p) => Boolean(p)),
      map((packages) => packages.map((p) => p.name).join(', ')),
    );

    this.selectedProducts$ = this.productSelectorService.selectedProducts$;
    this.selectedProductNames$ = this.productSelectorService.selectedProducts$.pipe(
      map((products) => {
        return products.map((p) => p.name).join(', ');
      }),
    );

    this.selectedStatusesNames$ = this.buildStatusFitlerChipNames();

    const cleanSelectedPackage$ = this.selectedPackages$.pipe(
      // Cast to unknown because Typescript thinks we are using the startWith that accepts a scheduler.
      // null is of type scheduler, so we cast it to unknown to avoid that issue
      startWith(null as unknown),
      map((p) => p || null),
    ) as Observable<Package[]>;
    const selectedProducts$ = this.productSelectorService.selectedProducts$;

    cleanSelectedPackage$.pipe(skipWhile((packages) => packages === null));
    selectedProducts$.subscribe(() => this.dialog.closeAll());
  }

  private filterBySalesTeamMemberIds(arrayOfSalesPersonIds: string[][]): void {
    const spIds = [];
    this.convert2DSalesTeamMembersToSalespeople(arrayOfSalesPersonIds, spIds);
    if (arrayOfSalesPersonIds && arrayOfSalesPersonIds.length > 0 && spIds.length === 0) {
      this.setSelectedSalesTeamMembers([NO_SALESPEOPLE_IN_TEAM_PLACEHOLDER]);
    } else {
      this.setSelectedSalesTeamMembers(spIds);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  private setSelectedSalespeople(salesPersonIds: string[]): void {
    this.spStore.setSelectedSalespeople(salesPersonIds);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  private setSelectedSalesTeamMembers(salesTeamMemberIds: string[]): void {}

  openAccountGroupFilter(): void {
    this.dialog.open(AccountFilterComponent, { width: '400px' });
  }

  clearAccountGroupFilter(): void {
    this.accountFilterService.clearAccountGroupFilter();
  }

  openOpportunityTypeFilter(): void {
    this.dialog.open(OpportunityTypeFilterComponent, { width: '400px' });
  }

  clearOpportunityTypeFilter(): void {
    this.opportunityTypeFilterService.clearOpportunityTypeSelection();
  }

  openSalespeopleSelector(): void {
    this.dialog.open(SalespersonMultiSelectComponent, { width: '400px' });
  }

  openSalesTeamSelector(): void {
    this.dialog.open(SalesTeamMultiSelectComponent, { width: '400px' });
  }

  clearSalespeopleFilters(): void {
    this.spStore.setSelectedSalespeople([]);
  }

  openPipelineStatusSelector(): void {
    this.dialog.open(PipelineStatusCheckboxComponent, { width: '400px' });
  }

  clearPipelineStatuses(): void {
    this.statusesFilterStore.resetFilter();
  }

  openTagSelector(): void {
    this.dialog.open(TagMultiSelectComponent, { width: '400px' });
  }
  clearSalesTeamFilters(): void {
    this.stStore.setSelectedSalesTeams([]);
  }

  clearTagFilter(): void {
    this.tagStore.clearTagsSelection();
  }

  clearExpectedRangeFilter(): void {
    this.expectedCloseFilterService.clearSelection();
    this.store.clearExpectedClose();
  }

  clearActualCloseFilter(): void {
    this.actualCloseFilterService.clearSelection();
    this.store.clearActualClose();
  }

  clearCreatedDateFilter(): void {
    this.createdDateFilterService.clearSelection();
    this.store.clearCreatedDate();
  }

  clearAssignedDateFilter(): void {
    this.assignedDateFilterService.clearSelection();
    this.store.clearAssignedDate();
  }

  async openExpectedCloseDateSelector(): Promise<void> {
    const expectedCloseFilter = await firstValueFrom(this.expectedCloseFilter$);

    const data: DateRangeSelectorDialogData = {
      beginDate: expectedCloseFilter?.start ?? this.CALENDAR_DEFAULTS.TODAY,
      endDate: expectedCloseFilter?.end ?? this.CALENDAR_DEFAULTS.ADD_ONE_WEEK,
      maxDate: this.CALENDAR_DEFAULTS.ADD_TWO_YEARS,
    };
    await this.dialogResultsToDateService(data, this.expectedCloseFilterService);
    this.store.sortByExpectedClose();
  }

  async openActualCloseSelector(): Promise<void> {
    const actualCloseFilter = await firstValueFrom(this.actualCloseFilter$);

    const dateRangeSelectorData = <DateRangeSelectorDialogData>{
      beginDate: actualCloseFilter?.start ?? this.CALENDAR_DEFAULTS.TODAY,
      endDate: actualCloseFilter?.end ?? this.CALENDAR_DEFAULTS.ADD_ONE_WEEK,
      maxDate: this.CALENDAR_DEFAULTS.ADD_TWO_YEARS,
    };
    await this.dialogResultsToDateService(dateRangeSelectorData, this.actualCloseFilterService);
    this.store.sortByActualClose();
  }

  async openCreatedDateSelector(): Promise<void> {
    const createdDateFilter = await firstValueFrom(this.createdDateFilter$);

    const dateRangeSelectorData = <DateRangeSelectorDialogData>{
      beginDate: createdDateFilter?.start ?? this.CALENDAR_DEFAULTS.START_OF_MONTH,
      endDate: createdDateFilter?.end ?? this.CALENDAR_DEFAULTS.TODAY,
      maxDate: this.CALENDAR_DEFAULTS.TODAY,
    };
    await this.dialogResultsToDateService(dateRangeSelectorData, this.createdDateFilterService);
    this.store.sortByCreatedDate();
  }

  async openAssignedDateSelector(): Promise<void> {
    const assignedDateFilter = await firstValueFrom(this.assignedDateFilter$);

    const dateRangeSelectorData = <DateRangeSelectorDialogData>{
      maxDate: this.CALENDAR_DEFAULTS.TODAY,
      beginDate: assignedDateFilter?.start ?? this.CALENDAR_DEFAULTS.START_OF_MONTH,
      endDate: assignedDateFilter?.end ?? this.CALENDAR_DEFAULTS.TODAY,
    };
    await this.dialogResultsToDateService(dateRangeSelectorData, this.assignedDateFilterService);
    this.store.sortByAssignedDate();
  }

  private async dialogResultsToDateService(
    dateRangeSelectorData: DateRangeSelectorDialogData,
    dateService: DateFilterServiceInterface,
  ): Promise<void> {
    const dialogRef = this.dialog.open(DateRangeSelectorDialogComponent, { data: dateRangeSelectorData });
    return firstValueFrom(
      dialogRef.afterClosed().pipe(
        filter((d) => Boolean(d)),
        map((results: DateRangeSelectorDialogData) => dateService.setDateRange(results.beginDate, results.endDate)),
      ),
    );
  }

  public openPackageSelector(): void {
    this.dialog.open(PipelineFilterPackageSelectorComponent, { width: '400px' });
  }

  public openProductSelector(): void {
    this.dialog.open(ProductSelectorComponent, {
      width: '400px',
    });
  }

  private convert2DSalesTeamMembersToSalespeople(salesTeamMembers: string[][], salespeople: string[]): void {
    for (const idx in salesTeamMembers) {
      if (salesTeamMembers[idx]) {
        salesTeamMembers[idx].map((newId) => {
          if (!salespeople.includes(newId)) {
            salespeople.push(newId);
          }
        });
      }
    }
  }

  clearPackageFilter(): void {
    this.pkgStore.clearPackageSelection();
  }

  clearProductsFilter(): void {
    this.productSelectorService.clearProductSelection();
  }

  private buildStatusFitlerChipNames(): Observable<string> {
    const i18nWonKey = 'FRONTEND.SALES_OPPORTUNITIES.WON_FILTER';
    const i18nLostKey = 'FRONTEND.SALES_OPPORTUNITIES.LOST_FILTER';
    const i18nOpenKey = 'FRONTEND.SALES_OPPORTUNITIES.OPEN_FILTER';
    return combineLatest([this.i18n.stream([i18nWonKey, i18nLostKey, i18nOpenKey]), this.selectedStatuses$]).pipe(
      map(([translations, s]: [any, PipelineStatuses]): string => {
        const won = s?.won ? translations[i18nWonKey] : '';
        const lost = s?.lost ? translations[i18nLostKey] : '';
        const open = s?.open ? translations[i18nOpenKey] : '';
        return [won, lost, open].filter(Boolean).join(', ');
      }),
    );
  }
}
